<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { formatNumber, formatDate } from "@core/helpers"
import type { Subscription } from "@modules/espace-entreprise/types/subscription"
import { useAuthStore } from "@core/stores/auth.store"
import { useResponsive } from "@core/composables"
import { storeToRefs } from "pinia"

// ================================
// INTERNATIONALIZATION
// ================================
const totalSourceTranslation =
    "Pack {max} source - {total} source utilisée | Pack {max} sources - {total} source utilisée | Pack {max} source - {total} sources utilisées | Pack {max} sources - {total} sources utilisées"

const { t } = useI18n({
    messages: {
        fr: {
            status: {
                active: "Abonnement actif",
                paused: "Abonnement en pause",
                canceled: "Abonnement annulé",
                past_due: "Abonnement actif - En retard de paiement",
                trialing: "Abonnement actif - Essai",
            },
            total_source: totalSourceTranslation,
            list_source: "Voir les sources",
            module_price: "+ {value} / {interval}",
            fields: {
                price: "Prix",
                facturation: "Facturation",
                start_date: "Date de début de l'abonnement",
                end_date: "Date de fin",
            },
            facturation: {
                month: "Mensuel",
                year: "Annuel",
                week: "Hebdomadaire",
                day: "Quotidien",
                quote: "Devis",
            },
            end_date_stripe: "Prochaine facturation",
            end_date_no_stripe: "Date de fin",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Détail de l'abonnement
     */
    subscription: Subscription
    /**
     * Nombre de sources facturées
     */
    sources: number
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

defineEmits<{
    "click:source": []
}>()

// ================================
// COMPOSABLES
// ================================
const { isMobile } = useResponsive()

// ================================
// STORES
// ================================
const authStore = useAuthStore()

const { isCompanyAdmin } = storeToRefs(authStore)

// ================================
// COMPUTED
// ================================

/**
 * Récupère le statut de l'abonnement avec son icône et son label
 */
const getStatus = computed(() => {
    const statusLabels: Record<string, string> = {
        active: t("status.active"),
        paused: t("status.paused"),
        canceled: t("status.canceled"),
        past_due: t("status.past_due"),
        trialing: t("status.trialing"),
    }

    const statusIcons: Record<string, string> = {
        active: "pi pi-check",
        paused: "pi pi-pause",
        canceled: "pi pi-ban",
        past_due: "pi pi-exclamation-triangle",
        trialing: "pi pi-clock",
    }

    // Le label du statut sans mention de Stripe (Stripe est une info technique, pas pertinente pour l'utilisateur)
    const label =
        statusLabels[props.subscription.status] || props.subscription.status

    return {
        icon: statusIcons[props.subscription.status] || "pi pi-circle",
        label,
    }
})

/**
 * Calcule le prix total de l'abonnement (prix de base + prix des modules)
 */
const getPrice = computed(() => {
    const pricingModules =
        props.subscription?.modules?.reduce((acc, module) => {
            acc += module.price
            return acc
        }, 0) || 0

    return formatNumber(props.subscription.price + pricingModules, {
        style: "currency",
        currency: "EUR",
    })
})

/**
 * Récupère le nom formaté de l'abonnement (sans le suffixe après "-")
 */
const getName = computed(() => {
    // Pour les abonnements legacy, on garde le nom complet
    if (props.subscription.legacy) {
        return props.subscription.name
    }

    // Pour les nouveaux abonnements, on retire le texte après "-"
    return (
        props.subscription.name?.split("-")?.[0]?.trim() ??
        props.subscription.name
    )
})

/**
 * Formate l'intervalle de facturation
 */
const getInvoiceInterval = computed(() => {
    const interval = props.subscription.invoice_interval
    const count = props.subscription.invoice_interval_count || 1

    const intervalLabels: Record<string, string> = {
        month: t("facturation.month"),
        year: t("facturation.year"),
        week: t("facturation.week"),
        day: t("facturation.day"),
        quote: t("facturation.quote"),
    }

    if (count > 1) {
        return `${count} ${intervalLabels[interval] || interval}`
    }

    return intervalLabels[interval] || interval
})

/**
 * Formate la date de fin selon si c'est Stripe ou non
 */
const getEndDateLabel = computed(() => {
    return props.subscription.stripe
        ? t("end_date_stripe")
        : t("end_date_no_stripe")
})

/**
 * Formate le texte du nombre de sources avec pluralisation
 */
const getTotalSourceText = computed(() => {
    const max = props.subscription.metadata?.sources || 0
    const total = props.sources

    // Sépare les 4 formes possibles de la traduction
    const forms = totalSourceTranslation.split(" | ")

    // Détermine l'index de la forme plurielle basé sur max et total
    // 0: max=1, total=1
    // 1: max>1, total=1
    // 2: max=1, total>1
    // 3: max>1, total>1
    const pluralIndex =
        max <= 1 && total <= 1
            ? 0
            : max > 1 && total <= 1
              ? 1
              : max <= 1 && total > 1
                ? 2
                : 3

    // Sélectionne la forme appropriée et remplace les variables
    const selectedForm = forms[pluralIndex] || forms[0]

    return selectedForm
        .replace("{max}", String(max))
        .replace("{total}", String(total))
})

/**
 * Nombre total de sources du pack (metadata de l'abonnement)
 */
const getPackMax = computed(() => props.subscription.metadata?.sources ?? 0)
</script>

<template>
    <div
        class="company-subscriptions-list-item flex flex-col gap-4 p-3 border-b border-gray-200 last:border-b-0"
    >
        <!-- ================================ -->
        <!-- TITRE AVEC STATUT -->
        <!-- ================================ -->
        <h1
            class="company-subscriptions-list-item__title flex items-center flex-wrap gap-4 text-lg font-semibold text-blue-700"
        >
            {{ getName }}
            <span
                class="company-subscriptions-list-item__status flex items-center gap-2 text-sm"
                :class="{
                    'text-green-600':
                        subscription.status === 'active' ||
                        subscription.status === 'trialing',
                    'text-orange-600': subscription.status === 'paused',
                    'text-red-600':
                        subscription.status === 'canceled' ||
                        subscription.status === 'past_due',
                }"
            >
                {{ getStatus.label }}
                <BaseIcon :icon="getStatus.icon" size="12px" />
            </span>
        </h1>

        <!-- ================================ -->
        <!-- INFORMATIONS SOURCES ET MODULES -->
        <!-- ================================ -->
        <div
            v-if="!subscription.legacy"
            class="company-subscriptions-list-item__info flex flex-col gap-4"
        >
            <!-- Nombre de sources (réservé aux administrateurs de l'entreprise) -->
            <div
                class="company-subscriptions-list-item__total-source flex items-center flex-wrap gap-3"
            >
                <p
                    v-if="isCompanyAdmin"
                    class="text-base font-semibold text-blue-700"
                >
                    {{ getTotalSourceText }}
                </p>
                <p
                    v-if="
                        isCompanyAdmin &&
                        !isMobile &&
                        subscription.status === 'active'
                    "
                    class="text-gray-400"
                >
                    •
                </p>
                <BaseButton
                    v-if="
                        (subscription.status === 'active' ||
                            subscription.status === 'past_due' ||
                            subscription.status === 'trialing') &&
                        isCompanyAdmin
                    "
                    variant="link"
                    size="small"
                    class="w-fit p-0!"
                    :label="t('list_source')"
                    @click="$emit('click:source')"
                />
            </div>

            <!-- Répartition des sources du pack (actives / supprimées ce mois / restant) -->
            <CompanySubscriptionSourcesMeterGroup
                v-if="
                    isCompanyAdmin &&
                    getPackMax > 0 &&
                    (subscription.status === 'active' ||
                        subscription.status === 'past_due' ||
                        subscription.status === 'trialing')
                "
                :subscription="subscription"
                :sources="sources"
                class="company-subscriptions-list-item__meter mt-2"
            />

            <!-- Liste des modules -->
            <div
                class="company-subscriptions-list-item__modules flex flex-col gap-4"
            >
                <div
                    v-for="(subModule, index) in subscription.modules"
                    :key="index"
                    class="company-subscriptions-list-item__modules-item flex items-center flex-wrap gap-3"
                >
                    <p class="text-base font-semibold text-blue-700">
                        {{ subModule.name }}
                    </p>
                    <p v-if="isCompanyAdmin" class="text-sm text-gray-600">
                        {{
                            t("module_price", {
                                value: formatNumber(
                                    subModule.price /
                                        (subModule.invoice_interval_count ?? 1),
                                    {
                                        style: "currency",
                                        currency: "EUR",
                                    }
                                ),
                                interval:
                                    subModule.invoice_interval === "month"
                                        ? "mois"
                                        : subModule.invoice_interval === "year"
                                          ? "an"
                                          : subModule.invoice_interval,
                            })
                        }}
                    </p>
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- CHAMPS D'INFORMATION -->
        <!-- ================================ -->
        <div
            class="company-subscriptions-list-item__fields grid gap-3 py-2"
            :class="{
                'grid-cols-1': true,
                'md:grid-cols-2': true,
                'lg:grid-cols-4': true,
            }"
        >
            <div class="flex flex-col gap-1.5">
                <p class="text-xs font-medium text-gray-500">
                    {{ t("fields.price") }}
                </p>
                <p class="text-sm text-gray-600">
                    <span v-if="isCompanyAdmin">{{ getPrice }} HT</span>
                    <span v-else>-</span>
                </p>
            </div>

            <div class="flex flex-col gap-1.5">
                <p class="text-xs font-medium text-gray-500">
                    {{ t("fields.facturation") }}
                </p>
                <p class="text-sm text-gray-600">{{ getInvoiceInterval }}</p>
            </div>

            <div class="flex flex-col gap-1.5">
                <p class="text-xs font-medium text-gray-500">
                    {{ t("fields.start_date") }}
                </p>
                <p class="text-sm text-gray-600">
                    {{ formatDate(subscription.date_start, "DD/MM/YYYY", "-") }}
                </p>
            </div>

            <div class="flex flex-col gap-1.5">
                <p class="text-xs font-medium text-gray-500">
                    {{ getEndDateLabel }}
                </p>
                <p class="text-sm text-gray-600">
                    {{ formatDate(subscription.date_end, "DD/MM/YYYY", "-") }}
                </p>
            </div>
        </div>
    </div>
</template>
